import React, { useState } from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import Skeleton from "../library/Skeleton";
import SettingLabel from "./SettingLabel";
import { formatCurrentPasswordError } from "./utils";

const EmailSettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const [previousEmail, setPreviousEmail] = useState<string>(null);

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.email === user.email) {
      onDone();
      return;
    }

    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        setPreviousEmail(values.email);
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          email: user.errors.email?.fullMessage,
          currentPassword: formatCurrentPasswordError(
            user.errors.currentPassword
          ),
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      user.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      setPreviousEmail(null);
      onEdit();
    }
  };

  const showNotSpecified = !isLoading && !user?.email;
  const showName = !isLoading && Boolean(user?.email);

  if (!isEditing) {
    return (
      <section data-test="email-setting-option">
        <Header>
          <SettingLabel>Email</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="22" />}
        {previousEmail && (
          <InfoMessage>
            Please confirm your email address by clicking on the link we&apos;ve
            just emailed to &quot;{previousEmail}&quot;
          </InfoMessage>
        )}
        {showNotSpecified && <div>Not specified</div>}
        {showName && <div>{user.email}</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    email: user.email,
    currentPassword: "",
  };

  const validation = Yup.object({
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Please enter an email address"),
    currentPassword: Yup.string().required("Please enter your password"),
  });

  return (
    <section data-test="email-setting-option">
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Email</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <TextInputField label="Email" hiddenLabel type="email" name="email" />
          <TextInputField
            label="Confirm password"
            type="password"
            name="currentPassword"
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

type FormValues = {
  email: string;
  currentPassword: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const InfoMessage = styled.div`
  font-size: 14px;
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.gray100};
  border-left: 3px solid ${({ theme }) => theme.colors.success};
`;

export default EmailSettingOption;
